/**
 * Sends webplatform-equivalent emails via NotificationService (Resend edge fn).
 * Failures are swallowed so UX flows are not blocked.
 *
 * In development builds, see `src/config/debug.overrides.ts` for logging and dry-run.
 */

import { env } from '@/config/env.ts'
import { debugOverrides } from '@/config/debug.overrides.ts'
import { notifications } from '@/services/index.ts'
import { getSupabaseClient } from '@/services/database/supabase.client.ts'
import type { NotificationResult } from './notification.types.ts'
import { NotificationChannel } from './notification.types.ts'
import {
  getAppUrl,
  vaultCreatedBenefactorEmail,
  vaultCreatedBeneficiaryEmail,
  countdownStartedBenefactorEmail,
  countdownStartedBeneficiaryEmail,
  notifyBeneficiaryVaultEmail,
  countdownResetBenefactorEmail,
  countdownResetBeneficiaryEmail,
  countdownReminderBenefactorEmail,
  countdownExpiredBeneficiaryEmail,
  subscriptionConfirmedEmail,
  subscriptionExpiringEmail,
  subscriptionExpiredEmail,
  formatDurationSeconds,
  formatTimeRemainingForReminder,
  formatExpiryDateFromUnixSeconds,
  subscriptionPlanName,
} from './webplatformEmailTemplates.ts'

const countdownExpiredInFlight = new Set<string>()

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function countdownExpiredKey(vaultId: number, email: string): string {
  return `${vaultId}:${normalizeEmail(email)}`
}

async function hasCountdownExpiredEmailBeenSent(
  vaultId: number,
  beneficiaryEmail: string,
): Promise<boolean> {
  try {
    const supabase = getSupabaseClient()
    const normalized = normalizeEmail(beneficiaryEmail)
    const { data, error } = await supabase
      .from('notifications')
      .select('id')
      .eq('channel', NotificationChannel.Email)
      .eq('status', 'sent')
      .eq('recipient', normalized)
      .contains('metadata', {
        event: 'countdown_expired',
        vaultId,
      })
      .limit(1)

    if (error) return false
    return Boolean(data && data.length > 0)
  } catch {
    return false
  }
}

async function sendTransactionalEmail(
  context: string,
  to: string,
  payload: { subject: string; text: string; html: string },
  options?: { metadata?: Record<string, unknown> },
): Promise<NotificationResult> {
  const trimmed = to?.trim() ?? ''
  if (!trimmed) {
    return {
      success: false,
      channel: NotificationChannel.Email,
      error: 'No recipient',
    }
  }

  if (env.enableDebug && debugOverrides.logTransactionalEmails) {
    console.info(`[BitsLegacy debug][email] ${context}`, {
      to: trimmed,
      subject: payload.subject,
      bodyPreview: payload.text.slice(0, 280),
    })
  }

  if (env.enableDebug && debugOverrides.dryRunTransactionalEmails) {
    return {
      success: true,
      channel: NotificationChannel.Email,
      messageId: 'debug-dry-run',
    }
  }

  try {
    return await notifications.sendEmail({
      to: normalizeEmail(trimmed),
      subject: payload.subject,
      body: payload.text,
      html: payload.html,
      metadata: options?.metadata,
    })
  } catch (e) {
    return {
      success: false,
      channel: NotificationChannel.Email,
      error: e instanceof Error ? e.message : 'Send failed',
    }
  }
}

async function safeSend(
  to: string,
  payload: { subject: string; text: string; html: string },
) {
  if (!to?.trim()) return
  await sendTransactionalEmail('webplatform', to, payload)
}

export async function sendVaultCreatedEmailsWebplatform(params: {
  vaultId: number
  vaultName?: string | null
  countdownMinutes: number
  benefactorAddress: string
  beneficiaryAddress: string
  beneficiaryEmail: string
  benefactorEmail?: string | null
  benefactorName?: string | null
  notifyBeneficiaryEmail?: boolean | null
  notifyBenefactorEmail?: boolean | null
}): Promise<void> {
  const appUrl = getAppUrl()
  const vaultName = params.vaultName?.trim() || 'Unnamed Vault'
  const bn = params.benefactorName?.trim() || 'Benefactor'

  if (
    params.notifyBenefactorEmail !== false &&
    params.benefactorEmail?.trim()
  ) {
    const p = vaultCreatedBenefactorEmail({
      appUrl,
      vaultId: params.vaultId,
      vaultName,
      countdownMinutes: params.countdownMinutes,
      beneficiaryWalletAddress: params.beneficiaryAddress,
      benefactorName: bn,
    })
    await safeSend(params.benefactorEmail, p)
  }

  if (
    params.notifyBeneficiaryEmail !== false &&
    params.beneficiaryEmail?.trim()
  ) {
    const p = vaultCreatedBeneficiaryEmail({
      appUrl,
      vaultId: params.vaultId,
      beneficiaryWalletAddress: params.beneficiaryAddress,
    })
    await safeSend(params.beneficiaryEmail, p)
  }
}

export async function sendCountdownStartedEmailsWebplatform(params: {
  vaultId: number
  countdownDurationSeconds: number
  benefactorAddress: string
  beneficiaryAddress: string
  benefactorEmail?: string | null
  beneficiaryEmail?: string | null
  benefactorName?: string | null
  notifyBenefactorEmail?: boolean | null
  notifyBeneficiaryEmail?: boolean | null
}): Promise<void> {
  const appUrl = getAppUrl()
  const durationText = formatDurationSeconds(params.countdownDurationSeconds)
  const benefactorName = params.benefactorName?.trim() || 'Benefactor'

  if (
    params.notifyBenefactorEmail !== false &&
    params.benefactorEmail?.trim()
  ) {
    const p = countdownStartedBenefactorEmail({
      appUrl,
      vaultId: params.vaultId,
      beneficiaryWalletAddress: params.beneficiaryAddress,
      benefactorName,
      durationText,
    })
    await safeSend(params.benefactorEmail, p)
  }

  if (
    params.notifyBeneficiaryEmail !== false &&
    params.beneficiaryEmail?.trim()
  ) {
    const p = countdownStartedBeneficiaryEmail({
      appUrl,
      vaultId: params.vaultId,
      beneficiaryWalletAddress: params.beneficiaryAddress,
      durationText,
    })
    await safeSend(params.beneficiaryEmail, p)
  }
}

export async function sendCountdownResetEmailsWebplatform(params: {
  vaultId: number
  benefactorEmail?: string | null
  beneficiaryEmail?: string | null
  benefactorName?: string | null
  notifyBenefactorEmail?: boolean | null
  notifyBeneficiaryEmail?: boolean | null
}): Promise<void> {
  const appUrl = getAppUrl()
  const bn = params.benefactorName?.trim() || 'Benefactor'

  if (
    params.notifyBenefactorEmail !== false &&
    params.benefactorEmail?.trim()
  ) {
    const p = countdownResetBenefactorEmail({
      appUrl,
      vaultId: params.vaultId,
      benefactorName: bn,
    })
    await safeSend(params.benefactorEmail, p)
  }

  if (
    params.notifyBeneficiaryEmail !== false &&
    params.beneficiaryEmail?.trim()
  ) {
    const p = countdownResetBeneficiaryEmail({
      appUrl,
      vaultId: params.vaultId,
    })
    await safeSend(params.beneficiaryEmail, p)
  }
}

export async function sendCountdownReminderToBenefactorWebplatform(params: {
  vaultId: number
  benefactorEmail: string
  benefactorName?: string | null
  timeRemainingSeconds: number
  notifyEmail?: boolean | null
}): Promise<NotificationResult | null> {
  if (params.notifyEmail === false || !params.benefactorEmail?.trim())
    return null
  const appUrl = getAppUrl()
  const timeText = formatTimeRemainingForReminder(params.timeRemainingSeconds)
  const p = countdownReminderBenefactorEmail({
    appUrl,
    vaultId: params.vaultId,
    benefactorName: params.benefactorName?.trim() || 'Benefactor',
    timeText,
  })
  const r = await sendTransactionalEmail(
    'countdownReminder',
    params.benefactorEmail,
    p,
  )
  return r.success ? r : null
}

export async function sendCountdownExpiredToBeneficiaryWebplatform(params: {
  vaultId: number
  beneficiaryEmail: string
  beneficiaryAddress: string
  notifyEmail?: boolean | null
}): Promise<NotificationResult | null> {
  if (params.notifyEmail === false || !params.beneficiaryEmail?.trim())
    return null

  const dedupeKey = countdownExpiredKey(params.vaultId, params.beneficiaryEmail)
  if (countdownExpiredInFlight.has(dedupeKey)) return null

  if (
    await hasCountdownExpiredEmailBeenSent(
      params.vaultId,
      params.beneficiaryEmail,
    )
  ) {
    return null
  }

  countdownExpiredInFlight.add(dedupeKey)
  const appUrl = getAppUrl()
  const p = countdownExpiredBeneficiaryEmail({
    appUrl,
    vaultId: params.vaultId,
    beneficiaryWalletAddress: params.beneficiaryAddress,
  })
  try {
    const r = await sendTransactionalEmail(
      'countdownExpired',
      params.beneficiaryEmail,
      p,
      {
        metadata: {
          event: 'countdown_expired',
          vaultId: params.vaultId,
          beneficiaryAddress: params.beneficiaryAddress,
        },
      },
    )
    return r.success ? r : null
  } finally {
    countdownExpiredInFlight.delete(dedupeKey)
  }
}

/** Resend / notify-beneficiary (template C). */
export async function sendNotifyBeneficiaryVaultEmailWebplatform(params: {
  vaultId: number
  beneficiaryEmail: string
  beneficiaryAddress: string
}): Promise<NotificationResult | null> {
  if (!params.beneficiaryEmail?.trim()) return null
  const p = notifyBeneficiaryVaultEmail({
    appUrl: getAppUrl(),
    vaultId: params.vaultId,
    beneficiaryWalletAddress: params.beneficiaryAddress,
  })
  return await sendTransactionalEmail(
    'notifyBeneficiary',
    params.beneficiaryEmail,
    p,
  )
}

export async function sendSubscriptionConfirmedEmailWebplatform(params: {
  to: string
  userName: string
  tier: number
  expiryTime: bigint
  transactionHash: string
  notifyEmail?: boolean | null
}): Promise<void> {
  if (params.notifyEmail === false || !params.to?.trim()) return
  const p = subscriptionConfirmedEmail({
    appUrl: getAppUrl(),
    userName: params.userName.trim() || 'User',
    planName: subscriptionPlanName(params.tier),
    expiryDate: formatExpiryDateFromUnixSeconds(params.expiryTime),
    transactionHash: params.transactionHash,
  })
  await safeSend(params.to, p)
}

export async function sendSubscriptionExpiringEmailWebplatform(params: {
  to: string
  userName: string
  tier: number
  expiryTime: bigint
  notifyEmail?: boolean | null
}): Promise<boolean> {
  if (params.notifyEmail === false || !params.to?.trim()) return false
  const p = subscriptionExpiringEmail({
    appUrl: getAppUrl(),
    userName: params.userName.trim() || 'User',
    planName: subscriptionPlanName(params.tier),
    expiryDate: formatExpiryDateFromUnixSeconds(params.expiryTime),
  })
  const r = await sendTransactionalEmail('subscriptionExpiring', params.to, p)
  return r.success
}

export async function sendSubscriptionExpiredEmailWebplatform(params: {
  to: string
  userName: string
  tier: number
  notifyEmail?: boolean | null
}): Promise<boolean> {
  if (params.notifyEmail === false || !params.to?.trim()) return false
  const p = subscriptionExpiredEmail({
    appUrl: getAppUrl(),
    userName: params.userName.trim() || 'User',
    planName: subscriptionPlanName(params.tier),
  })
  const r = await sendTransactionalEmail('subscriptionExpired', params.to, p)
  return r.success
}
